import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar/Navbar';
import Hero from '../components/Hero/Hero';
import Modal from '../components/Modal/Modal';
import DownloadSection from '../components/DownloadSection/DownloadSection';
import PulseDemo from '../components/PulseDemo/PulseDemo';
import appConfig from '../config/appConfig';
import useInView from '../hooks/useInView';
import styles from './HomePage.module.css';

const TAGLINE = 'Your story with the people you meet.';
const TAGLINE_SUB = 'Create plans, show up together, and build a living timeline.';
const HERO_DESC =
  'Pictures get buried in your gallery. Timeline brings them back - create a plan, invite people, share moments, and keep shared memories in one place.';

/** Set to true to show the profile / shared memories section */
const PROFILE_SECTION_ENABLED = false;

function SectionTitle({ eyebrow, title, desc }) {
  return (
    <div className={styles.sectionTitle}>
      <div className={styles.eyebrow}>{eyebrow}</div>
      <h2 className={styles.h2}>{title}</h2>
      {desc ? <p className={styles.p}>{desc}</p> : null}
    </div>
  );
}

function Reveal({ children, delay = 0 }) {
  const { ref, inView } = useInView({ threshold: 0.15, rootMargin: '0px 0px -12% 0px', once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 18, filter: 'blur(10px)' }}
      animate={inView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : undefined}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function TermsContent() {
  return (
    <div>
      <h3>Overview</h3>
      <p>
        Timeline is a social app for plans and memories. Create a plan, invite people,
        show up together, keep photos connected to everyone who was there, and build a shared timeline.
        By accessing or using Timeline, you agree to these Terms of Service and our Privacy Policy.
      </p>
      <p>
        You must be at least <strong>18 years old</strong> to use Timeline. If we discover that a user
        is under 18, the account may be terminated.
      </p>

      <h3>Account and eligibility</h3>
      <p>
        Accounts are created using Google Sign-In. You agree to provide accurate information and
        maintain only one account. You are responsible for safeguarding your login credentials.
        We may suspend or terminate accounts that violate these terms or applicable laws.
      </p>

      <h3>Acceptable use</h3>
      <ul>
        <li>Be respectful and act in good faith toward other users.</li>
        <li>Harassment, bullying, hate speech, threats, or impersonation are strictly prohibited.</li>
        <li>Do not share illegal, obscene, defamatory, or infringing content.</li>
        <li>Do not use bots, scraping tools, or attempt to bypass app safeguards.</li>
        <li>Do not misuse deep links or attempt to access other users’ data without permission.</li>
      </ul>

      <h3>User content</h3>
      <p>
        You retain ownership of the content you upload, such as photos, bios, plans, and messages.
        By using Timeline, you grant us a non-exclusive, royalty-free licence to store, display,
        and process this content solely to operate and improve the service.
      </p>

      <h3>Plans and discovery</h3>
      <p>
        Timeline lets you create public, private, or invite-only plans, invite people, and browse a global feed of plans.
        Visibility preferences are controlled within the app. Details are in our Privacy Policy.
      </p>

      <h3>Premium features</h3>
      <p>
        Certain features may be available through paid subscriptions. Payments and refunds are managed
        through the app stores and subject to their policies.
      </p>

      <h3>Disclaimers</h3>
      <p>
        Timeline is provided “as is” without warranties of any kind. We do not guarantee connections
        or uninterrupted service. Interactions with other users are at your own risk.
      </p>

      <h3>Limitation of liability</h3>
      <p>
        To the maximum extent permitted by law, Timeline shall not be liable for indirect,
        incidental, or consequential damages. Our total liability will not exceed the amount
        you paid us in the last twelve months, if any.
      </p>

      <h3>Termination</h3>
      <p>
        You may delete your account at any time by requesting us. We may suspend or terminate accounts that violate
        these terms, engage in misuse, or for legal or operational reasons.
      </p>

      <h3>Governing law</h3>
      <p>
        These terms are governed by the laws of India. Any disputes shall be subject to the
        jurisdiction of Indian courts.
      </p>

      <h3>Contact</h3>
      <p>
        For questions, support, or legal inquiries, email -
        <a href={`mailto:${appConfig.supportEmail}`}>{appConfig.supportEmail}</a>.
      </p>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div>
      <h3>Privacy-first by design</h3>
      <p>
        Timeline respects your privacy. This Privacy Policy explains what data we
        collect, how we use it, and your rights when you use the Timeline mobile application.
      </p>
      <p>
        You must be at least 18 years old to use Timeline. We do not knowingly collect data from users
        under 18. If we learn that such data has been collected, we will delete it.
      </p>

      <h3>Data we collect</h3>
      <p>We may collect the following categories of data when you use the Timeline app:</p>
      <ul>
        <li>
          <strong>Account and profile data:</strong> Google account identifier, display name, email
          address, profile photo, username, bio, photos, age range, gender, and optional social links.
        </li>
        <li>
          <strong>Plans and messages:</strong> Plan details, invite requests, and related content to
          run plans, timelines, and safety features.
        </li>
        <li>
          <strong>Device and usage data:</strong> Device type, OS version, app version, crash logs,
          feature usage, and push notification tokens.
        </li>
      </ul>

      <h3>How we use your data</h3>
      <ul>
        <li>Create and manage your account and profile.</li>
        <li>Provide plans, invites, memories, and timeline features.</li>
        <li>Send notifications related to app activity (join requests, messages).</li>
        <li>Process premium subscriptions through app stores.</li>
        <li>Improve app performance, stability, and user experience.</li>
        <li>Comply with legal obligations and enforce our Terms of Service.</li>
      </ul>

      <h3>Analytics</h3>
      <p>
        We may use privacy-focused analytics and crash reporting tools (such as Firebase Analytics
        and Crashlytics) to understand app usage and fix issues. These tools collect aggregated and
        technical data and do not sell your personal information.
      </p>

      <h3>Sharing with third parties</h3>
      <p>
        We do not sell your personal data. We may share data with trusted service providers such as
        Google (Sign-In, Play Store, Firebase) strictly to operate the service, or when required
        by law.
      </p>

      <h3>Data retention</h3>
      <p>
        We retain your data while your account is active. When you delete your account, your personal
        data is deleted or anonymised within a reasonable period, subject to legal and security
        requirements.
      </p>

      <h3>Your rights</h3>
      <p>
        You may access, correct, delete, your personal data by contacting us. You may also object to or restrict certain processing where permitted by
        law.
      </p>

      <h3>Security</h3>
      <p>
        We use industry-standard security measures to protect your data. We encrypt chat messages. However, no system can be
        guaranteed to be completely secure.
      </p>

      <h3>International processing</h3>
      <p>
        Your data may be processed in India and other countries where our service providers operate,
        with appropriate safeguards in place as required by law.
      </p>

      <h3>Policy updates</h3>
      <p>
        We may update this Privacy Policy from time to time. Continued use of the app after updates
        means you accept the revised policy.
      </p>

      <h3>Support</h3>
      <p>
        For privacy questions or requests, email-
        <a href={`mailto:${appConfig.supportEmail}`}>{appConfig.supportEmail}</a>.
      </p>
    </div>
  );
}

function SupportContent() {
  return (
    <div>
      <h3>Support</h3>
      <p>
        Timeline is built around real plans, clear privacy controls, and memories that live on your timeline.
      </p>
      <h3>Contact</h3>
      <ul>
        <li>
          Support: <a href={`mailto:${appConfig.supportEmail}`}>{appConfig.supportEmail}</a>
        </li>
      </ul>
    </div>
  );
}

const STEPS = [
  {
    icon: '📅',
    title: 'Create a plan',
    body: 'Set what you’re doing and when - coffee, dinner, a trip, a night out.',
  },
  {
    icon: '🤝',
    title: 'Invite people',
    body: 'Invite friends and family, or let people request to join from the feed.',
  },
  {
    icon: '📸',
    title: 'Post memories',
    body: 'When the plan ends, add photos. They stay on your shared timeline.',
  },
];

const FEATURES = [
  {
    title: 'Public',
    desc: 'Anyone can see and join - open hangouts, brunch, rides, and more.',
    tone: 'public',
  },
  {
    title: 'Private',
    desc: 'Anyone can request to join. You choose to approve or reject each request.',
    tone: 'private',
  },
  {
    title: 'Invite only',
    desc: 'Share your plan with particular friends. Only they can see the invite.',
    tone: 'invite',
  },
];

export default function HomePage() {
  const [activeModal, setActiveModal] = useState(null);

  const modal = useMemo(() => {
    if (!activeModal) return null;
    const map = {
      terms: { title: 'Terms & Conditions', body: <TermsContent /> },
      privacy: { title: 'Privacy Policy', body: <PrivacyContent /> },
      support: { title: 'Support', body: <SupportContent /> },
    };
    return map[activeModal] || null;
  }, [activeModal]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className={styles.page}>
      <Navbar />

      <Hero
        tagline={TAGLINE}
        subTagline={TAGLINE_SUB}
        description={HERO_DESC}
        onPrimary={() => scrollTo('download')}
        onSecondary={() => scrollTo('plans')}
      />

      <main className={styles.main}>
        <section id="how-it-works" className={styles.section}>
          <div className="container">
            <SectionTitle
              eyebrow="How Timeline works"
              title="Create something. Show up together. Keep what happened."
              desc="Your gallery stores pictures. Timeline keeps the story - who was there and the memories you made together."
            />

            <div className={styles.grid3}>
              {STEPS.map((step, i) => (
                <Reveal key={step.title} delay={i * 0.05}>
                  <div className={styles.card}>
                    <div className={styles.icon}>{step.icon}</div>
                    <h3 className={styles.h3}>{step.title}</h3>
                    <p className={styles.pSmall}>{step.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="plans" className={`${styles.section} ${styles.sectionAlt}`}>
          <div className="container">
            <SectionTitle
              eyebrow="Plans"
              title="Make the plan. Bring the people."
              desc="Public, private, or invite only. Invite who matters, manage requests, and keep every plan in one place."
            />
            <Reveal>
              <PulseDemo />
            </Reveal>
          </div>
        </section>

        <section id="feed" className={styles.section}>
          <div className="container">
            <SectionTitle
              eyebrow="Global feed"
              title="Discover plans worth showing up for"
              desc="Scroll the feed, find something happening, and request to join. Less scrolling - more real moments."
            />

            <div className={styles.discoverLayout}>
              <Reveal>
                <div className={styles.feedPreviewCard}>
                  <div className={styles.mapTop}>
                    <div className={styles.mapTitle}>Feed</div>
                    <div className={styles.mapSub}>Plans from people around you and beyond</div>
                  </div>
                  <div className={styles.feedPreviewList}>
                    {[
                      { title: 'Weekend trip', place: 'Two days with friends', when: 'Fri · 6:00 am', host: 'Mansi', cover: '/memories/memory-5.png', action: 'Request', privacy: 'Private' },
                      { title: 'Sunday Brunch', place: 'Brunch with friends', when: 'Sat · 11:00 am', host: 'Aanya', cover: '/memories/memory-1.png', action: 'Join', privacy: 'Public' },
                      { title: 'Friday Dinner', place: 'Cozy dinner plans', when: 'Sun · 8:00 pm', host: 'Dev', cover: '/memories/memory-3.png', action: 'Invite only', privacy: 'Invite only' },
                    ].map((p) => (
                      <div key={p.title} className={styles.feedPreviewItem}>
                        <img src={p.cover} alt="" className={styles.feedPreviewThumb} />
                        <div>
                          <strong>{p.title}</strong>
                          <div>{p.place}</div>
                          <div>{p.host} · {p.when} · {p.privacy} · {p.action}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>

              <div className={styles.stack}>
                {FEATURES.map((f, i) => (
                  <Reveal key={f.title} delay={0.04 * i}>
                    <div className={styles.cardWide}>
                      <div className={styles.row}>
                        <div>
                          <div className={styles.smallKicker}>Plan types</div>
                          <h3 className={styles.h3}>{f.title}</h3>
                          <p className={styles.pSmall}>{f.desc}</p>
                        </div>
                        <div className={`${styles.chip} ${styles[`chip_${f.tone}`]}`}>{f.title}</div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          className={`${styles.section} ${styles.sectionAlt} ${!PROFILE_SECTION_ENABLED ? styles.sectionHidden : ''}`}
          aria-hidden={!PROFILE_SECTION_ENABLED}
        >
          <div className="container">
            <SectionTitle
              eyebrow="Your profile"
              title="Private profile. Shared memories."
              desc="Your profile is yours. Timeline and photos stay private to you. After a plan, people who were part of it can see and add common photos to that memory together."
            />
            <div className={styles.grid3}>
              <Reveal>
                <div className={styles.card}>
                  <div className={styles.icon}>📷</div>
                  <h3 className={styles.h3}>Add event photos</h3>
                  <p className={styles.pSmall}>When a plan ends, add your pictures. Everyone who was part of that plan can view and add common photos. One shared memory, built together.</p>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <div className={styles.card}>
                  <div className={styles.icon}>💬</div>
                  <h3 className={styles.h3}>Plans & requests</h3>
                  <p className={styles.pSmall}>See your upcoming plans and requests from others: when and what, all in the Plans tab.</p>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className={styles.card}>
                  <div className={styles.icon}>✦</div>
                  <h3 className={styles.h3}>Profile</h3>
                  <p className={styles.pSmall}>Your profile is private: name, bio, timeline, and photos. Plan memories can include common photos visible to everyone who joined that plan.</p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section id="download" className={styles.section}>
          <div className="container">
            <DownloadSection
              eyebrow="Get Timeline"
              title="Timeline is live on iOS and Android."
              desc="Scan a QR code to download and start creating plans."
            />
          </div>
        </section>

        <footer className={styles.footer}>
          <div className="container">
            <div className={styles.footerTop}>
              <div>
                <div className={styles.footerBrand}>Timeline</div>
                <div className={styles.footerTag}>Your timeline. Your memories. Your lane.</div>
              </div>
              <div className={styles.footerLinks}>
                <button type="button" onClick={() => setActiveModal('terms')}>Terms & Conditions</button>
                <button type="button" onClick={() => setActiveModal('privacy')}>Privacy Policy</button>
                <button type="button" onClick={() => setActiveModal('support')}>Support</button>
              </div>
            </div>
            <div className={styles.footerBottom}>
              <div className={styles.footerFine}>© {new Date().getFullYear()} Timeline. All rights reserved.</div>
              <div className={styles.footerFine}>
                Contact: <a href={`mailto:${appConfig.contactEmail}`}>{appConfig.contactEmail}</a>
              </div>
            </div>
          </div>
        </footer>
      </main>

      <Modal isOpen={Boolean(modal)} onClose={() => setActiveModal(null)} title={modal?.title || ''}>
        {modal?.body}
      </Modal>
    </div>
  );
}
